/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';
import ReactSelect from 'react-select';
import { useLang } from 'hooks/lang';

import { User } from 'entities';

import { Column, Row, Avatar, Skeleton, Tag } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';

import { useTeam } from 'providers/Team';

import { getBaseReactSelectStyle } from './styles';

import { getFirstAndLastName } from 'utils/text';
import { sortByName } from 'utils/array';

function ChooseOwnerLabel({ user }: { user: User }) {
  const { t } = useLang();

  return (
    <Row>
      <div className="flex items-center [_.choose-owner-select\_\_value-container_&]:-ml-[15px] [_.choose-owner-select\_\_value-container_&]:scale-[1.5]">
        <UserAvatar user={user} />
      </div>

      <Column gap="0">
        <span>{getFirstAndLastName(user.name)}</span>

        {user.type === 'LEADER' && (
          <Tag name={t('leader')} color="primary-light" className="w-fit" size="tiny" />
        )}
      </Column>
    </Row>
  );
}

function Loading() {
  return (
    <Row gap="0">
      <div className="-ml-[15px] scale-[1.5]">
        <Skeleton circle width={40} height={40} />
      </div>

      <Skeleton width={220} height={46} />
    </Row>
  );
}

function Placeholder({ text }: { text?: string }) {
  const { t } = useLang();

  return (
    <Row>
      <div className="flex items-center [_.choose-owner-select\_\_value-container_&]:-ml-[15px] [_.choose-owner-select\_\_value-container_&]:scale-[1.5]">
        <Avatar fallback="no-user-avatar" size="large" />
      </div>

      <span>{text || t('choose-owner')}</span>
    </Row>
  );
}

interface OwnerSelectProps {
  value: string;
  onChange: (value: string) => void;
  users?: User[];
  placeholder?: string;
  loading?: boolean;
}

interface OwnerSelectOption extends User {
  value: string;
  label: string;
}

export function OwnerSelect({ value, onChange, users, placeholder, loading }: OwnerSelectProps) {
  const { teamUsers } = useTeam();

  const reactSelectOptions = sortByName(users || teamUsers).map(({ id, name, ...rest }) => ({
    value: id,
    label: name,
    id,
    name,
    ...rest,
  })) as OwnerSelectOption[];

  const reactSelectValue = reactSelectOptions.find((option) => option.value === value);

  const handleChange = useCallback(
    (option: OwnerSelectOption | null) => {
      onChange(option?.value || '');
    },
    [onChange],
  );

  if (loading) return <Loading />;

  return (
    <div className="[&_.choose-owner-select__option--is-focused]:bg-hover">
      <ReactSelect<OwnerSelectOption>
        options={reactSelectOptions}
        value={reactSelectValue}
        onChange={handleChange}
        formatOptionLabel={(user) => <ChooseOwnerLabel user={user} />}
        placeholder={<Placeholder text={placeholder} />}
        className="overflow-visible"
        styles={getBaseReactSelectStyle() as any}
        classNamePrefix="choose-owner-select"
      />
    </div>
  );
}
